import type { GeneratedSample } from './types';

/**
 * Synthesized NES-style kick drum.
 *
 * Recipe: a sine wave whose frequency sweeps quickly from ~140 Hz down to
 * ~50 Hz, multiplied by a fast exponential decay envelope. The whole thing
 * lasts ~80 ms. This gives the characteristic "thump" you hear in MM 2/3
 * kicks without sampling anything.
 *
 * The pitch sweep is what differentiates this from a single tom hit — your
 * ear hears the descending pitch and reads it as a kick body.
 */
export function generateKick(sampleRate = 22_050): GeneratedSample {
  const durationSec = 0.08;
  const length = Math.floor(durationSec * sampleRate);
  const data = new Float32Array(length);

  const startFreq = 140;
  const endFreq = 50;
  const ampDecay = 22; // higher = faster decay
  const pitchDecay = 35;

  let phase = 0;
  for (let i = 0; i < length; i++) {
    const t = i / sampleRate;
    // Exponential pitch sweep from startFreq → endFreq.
    const freq = endFreq + (startFreq - endFreq) * Math.exp(-pitchDecay * t);
    phase += (freq * 2 * Math.PI) / sampleRate;
    const env = Math.exp(-ampDecay * t);
    data[i] = Math.sin(phase) * env;
  }

  // Light limiting in case the peak edges past 1.0 due to envelope rounding.
  for (let i = 0; i < length; i++) {
    const v = data[i]!;
    data[i] = Math.max(-1, Math.min(1, v));
  }

  return { data, sampleRate, name: 'kick' };
}
